// src/crm/campaigns/CampaignsList.tsx
import { Link } from 'react-router-dom'
import type { Campaign } from '../../lib/types'
import { useDeleteCampaign } from '../hooks/useCampaigns'

const STATUS_LABEL: Record<Campaign['status'], string> = {
  planning: 'Planning',
  active: 'Active',
  done: 'Terminée',
}

const STATUS_COLOR: Record<Campaign['status'], string> = {
  planning: 'var(--muted)',
  active: 'var(--arancio)',
  done: 'var(--bronze)',
}

interface CampaignsListProps {
  campaigns: Campaign[]
  appId: string
  onEdit: (c: Campaign) => void
}

function formatDate(d: string | null): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function CampaignsList({ campaigns, appId, onEdit }: CampaignsListProps) {
  const deleteMutation = useDeleteCampaign(appId)

  function handleDelete(c: Campaign) {
    if (!window.confirm(`Supprimer la campagne « ${c.name} » ? Cette action est irréversible.`)) return
    deleteMutation.mutate(c.id)
  }

  if (campaigns.length === 0) {
    return (
      <div style={{
        padding: '3rem',
        textAlign: 'center',
        color: 'var(--muted)',
        fontSize: '0.875rem',
      }}>
        Aucune campagne pour cette app. Créez-en une.
      </div>
    )
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid var(--line)' }}>
          {(['Nom', 'Statut', 'Budget', 'Dates', ''] as const).map((h) => (
            <th key={h} style={{
              textAlign: 'left',
              padding: '0.5rem 0.75rem',
              color: 'var(--muted)',
              fontWeight: 500,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {campaigns.map((c) => (
          <tr
            key={c.id}
            style={{ borderBottom: '1px solid var(--line)' }}
          >
            <td style={{ padding: '0.75rem', color: 'var(--fg)', fontWeight: 500 }}>
              <Link to={`/crm/campaigns/${c.id}`} style={{ color: 'var(--fg)', textDecoration: 'none', fontWeight: 500 }}>
                {c.name}
              </Link>
              {c.goal && (
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.1875rem' }}>
                  {c.goal}
                </div>
              )}
            </td>
            <td style={{ padding: '0.75rem' }}>
              <span style={{
                display: 'inline-block',
                padding: '0.125rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: STATUS_COLOR[c.status],
                background: 'var(--elevated)',
                border: `1px solid ${STATUS_COLOR[c.status]}40`,
              }}>
                {STATUS_LABEL[c.status]}
              </span>
            </td>
            <td style={{ padding: '0.75rem', color: 'var(--fg)', fontFamily: 'var(--mono)', fontSize: '0.8125rem' }}>
              {c.budgetAmount !== null
                ? `${c.budgetAmount.toLocaleString('fr-FR')} ${c.currency}`
                : <span style={{ color: 'var(--dim)' }}>—</span>}
            </td>
            <td style={{ padding: '0.75rem', color: 'var(--muted)', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
              {formatDate(c.startDate)} → {formatDate(c.endDate)}
            </td>
            <td style={{ padding: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={() => onEdit(c)} style={btnIcon} title="Modifier">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(c)}
                  disabled={deleteMutation.isPending}
                  style={{ ...btnIcon, color: 'var(--arancio)' }}
                  title="Supprimer"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const btnIcon: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--line)',
  borderRadius: '5px',
  padding: '0.25rem',
  cursor: 'pointer',
  color: 'var(--muted)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}
